import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MarketCalculusCard } from '../../../components/preview/Preview'
import { P7Services } from '../../../services/McDataServices'
import { getFirm } from '../../../hooks/useFirmDetails'
import { useSDSsearchParams } from '../../../store/searchDataStore'
import { Tooltip, Typography } from '@mui/material'
import { createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getFacetedRowModel, getFacetedUniqueValues, getFacetedMinMaxValues, useReactTable } from '@tanstack/react-table'
import { TablePaginationComponentServerSide } from '../../../mailTemplate/src/components/atomic/molecules/TablePaginationComponent'
import LoadingOverlay from '../../../mailTemplate/src/components/atomic/atoms/LoadingOverlay'
import '../../../assets/scss/apps/_serverside-table.scss'

const sanitize = (val) => (!val || val === '.') ? '' : val

const httpPrefixer = (url) => {
  return (url?.indexOf('://') === -1) ? `http://${url}` : url
}

const columnHelper = createColumnHelper()

const ExporterTableSection = ({ isActive }) => {
  const { t } = useTranslation()
  const searchParams = useSDSsearchParams()

  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)
  const [rowCount, setRowCount] = useState(0)
  const [serverSidePagination, setServerSidePagination] = useState({ pageSize: 10 })

  const requestRef = useRef(null)

  useEffect(() => {
    if (!isActive) return
    if (!searchParams?.hsCode || !searchParams?.countryToExport || !searchParams?.yourCountry) return

    const controller = new AbortController()
    requestRef.current = controller

    const fetchData = async () => {
      try {
        setLoading(true)
        const params = {
          hsCodes: searchParams.hsCode.toString(),
          importerCountryCode: searchParams.countryToExport,
          currentCountryCode: searchParams.yourCountry,
          productName: searchParams.productDesc || undefined,
          page: serverSidePagination.pageIndex ?? 0,
          pageSize: serverSidePagination.pageSize ?? 10
        }
        const response = await P7Services.getP7Exporters(params, { signal: controller.signal })
        if (controller.signal.aborted || !response) return

        const p7Exporters = response.p7Exporters
        if (!p7Exporters || typeof p7Exporters !== 'object') {
          setTableData([])
          setRowCount(0)
          return
        }

        const firms = p7Exporters.firms || p7Exporters
        const totalCount = p7Exporters.totalCount

        const exporterEntries = Object.values(firms)
        const urlCodes = exporterEntries
          .map(container => container.firm?.url_code)
          .filter(Boolean)

        if (totalCount != null) {
          setRowCount(totalCount)
        } else {
          const pageSize = serverSidePagination.pageSize ?? 10
          const currentPage = serverSidePagination.pageIndex ?? 0
          if (exporterEntries.length < pageSize) {
            setRowCount(currentPage * pageSize + exporterEntries.length)
          } else {
            setRowCount((currentPage + 2) * pageSize)
          }
        }

        if (urlCodes.length > 0 && !controller.signal.aborted) {
          const firmPromises = urlCodes.map(urlCode =>
            getFirm(urlCode).catch(() => null)
          )
          const firms = await Promise.all(firmPromises)
          if (controller.signal.aborted) return

          const rows = firms
            .filter(Boolean)
            .map((firm, index) => ({
              id: index,
              urlCode: firm.urlCode || firm.url_code || '',
              companyName: sanitize(firm.name) || '',
              website: sanitize(firm.web) || '',
              emailList: (firm.emailList?.length > 0) ? firm.emailList : (sanitize(firm.e_mail) ? [firm.e_mail] : [])
            }))

          setTableData(rows)
        } else {
          setTableData([])
        }
      } catch (error) {
        if (error?.name !== 'AbortError' && error?.name !== 'CanceledError') {
          console.error('Exporter data fetch error:', error)
          setTableData([])
          setRowCount(0)
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    fetchData()
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, searchParams?.hsCode, searchParams?.countryToExport, searchParams?.yourCountry, searchParams?.productDesc, serverSidePagination])

  const columns = useMemo(() => [
    columnHelper.accessor('id', {
      header: t('table_data_table_column_ranking'),
      cell: ({ row }) => (serverSidePagination.pageIndex ?? 0) * (serverSidePagination.pageSize ?? 10) + row.original.id + 1,
      size: 60
    }),
    columnHelper.accessor('companyName', {
      header: t('mailTemplate.wizard.firmList.firmName'),
      cell: ({ row }) => (
        <Typography color="text.primary" className="font-medium">
          {row.original.companyName}
        </Typography>
      )
    }),
    columnHelper.accessor('website', {
      header: t('mailTemplate.wizard.firmList.website'),
      cell: ({ row }) => {
        const web = row.original.website
        if (!web) return '-'
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <a href={httpPrefixer(web)} target="_blank" rel="noreferrer">
              <Typography className="font-medium" style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {web}
              </Typography>
            </a>
          </div>
        )
      }
    }),
    columnHelper.accessor('emailList', {
      header: t('mailTemplate.wizard.firmList.email'),
      cell: ({ row }) => {
        const emailList = row.original.emailList
        if (!emailList?.length) return '-'
        // Normalize: split any semicolon-separated values into individual emails
        const emails = emailList.flatMap(e => e.split(';')).map(e => e.trim()).filter(Boolean)
        if (!emails.length) return '-'
        const visible = emails.slice(0, 2)
        const remaining = emails.slice(2)
        const lastVisible = visible[visible.length - 1]
        return (
          <div>
            {visible.slice(0, -1).map((email, i) => (
              <Typography key={i} color="text.primary" className="font-medium" style={{ fontSize: 'inherit' }}>
                {email}
              </Typography>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Typography color="text.primary" className="font-medium" style={{ fontSize: 'inherit' }}>
                {lastVisible}
              </Typography>
              {remaining.length > 0 && (
                <Tooltip
                  title={
                    <div style={{ whiteSpace: 'pre-line' }}>
                      {remaining.join('\n')}
                    </div>
                  }
                  arrow
                >
                  <Typography color="primary" className="font-medium" style={{ cursor: 'pointer', fontSize: 'inherit' }}>
                    +{remaining.length}
                  </Typography>
                </Tooltip>
              )}
            </div>
          </div>
        )
      }
    })
  ], [t, serverSidePagination])

  const table = useReactTable({
    data: tableData,
    rowCount,
    columns,
    state: { serverSidePagination },
    manualPagination: true,
    enableRowSelection: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    autoResetPageIndex: false,
    getRowId: row => row.id
  })

  return (
    <MarketCalculusCard>
      <div className="my-3">
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '400px', height: '100%' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {loading && <LoadingOverlay/>}
            <div className="overflow-x-auto" style={{ flex: 1 }}>
              <table className="serverside-table">
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th key={header.id} style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())
                        }
                      </th>
                    ))}
                  </tr>
                ))}
                </thead>
                {table.getFilteredRowModel().rows.length === 0
                  ? (
                    <tbody>
                    <tr>
                      <td colSpan={table.getVisibleFlatColumns().length} className="text-center">
                        {!loading && t('mailTemplate.wizard.firmList.noData')}
                      </td>
                    </tr>
                    </tbody>
                    )
                  : (
                    <tbody>
                    {table.getRowModel().rows.map(row => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                        ))}
                      </tr>
                    ))}
                    </tbody>
                    )
                }
              </table>
            </div>
            <div style={{ flexShrink: 0, marginTop: 'auto' }}>
              <TablePaginationComponentServerSide
                infoText1={t('mailTemplate.wizard.firmList.tableInfo_1')}
                infoText2={t('mailTemplate.wizard.firmList.tableInfo_2')}
                count={rowCount}
                pageIndex={serverSidePagination?.pageIndex || 0}
                pageSize={serverSidePagination?.pageSize || 10}
                setPageIndex={(val) => {
                  setServerSidePagination({
                    pageSize: serverSidePagination?.pageSize || 10,
                    pageIndex: val
                  })
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </MarketCalculusCard>
  )
}

export default ExporterTableSection
